import { supabase, isSupabaseConfigured } from './supabase';

export type SiteAuthUser = {
  id: string;
  email: string;
  name: string | null;
  role: { id: number; name: string; slug: string } | null;
  permissions: string[];
};

const DEFAULT_ROLE_ID = 2; // 企業

/** プロフィールがなければ作成（role_id = 企業）、その後ロール・権限を取得 */
export async function ensureProfileAndGetUser(
  userId: string,
  email: string
): Promise<SiteAuthUser | null> {
  if (!isSupabaseConfigured() || !supabase) return null;

  const { data: profile, error: selectError } = await supabase
    .from('site_user_profiles')
    .select('name, role_id')
    .eq('user_id', userId)
    .single();

  if (selectError && selectError.code !== 'PGRST116') {
    console.error('[siteAuth] profile select error:', selectError);
    const hint =
      selectError.code === '42P01' || selectError.message?.includes('does not exist')
        ? 'site_user_profiles テーブルがありません。Supabase の SQL Editor で docs/SUPABASE_SQL_実行手順.md の SQL を実行してください。'
        : selectError.message || 'プロフィールの取得に失敗しました。';
    throw new Error(hint);
  }

  let currentProfile = profile;

  if (!currentProfile) {
    const { error: insertError } = await supabase.from('site_user_profiles').insert({
      user_id: userId,
      name: null,
      role_id: DEFAULT_ROLE_ID,
    });
    if (insertError) {
      console.error('[siteAuth] profile insert error:', insertError);
      const msg =
        insertError.code === '42P01'
          ? 'サイト用のテーブルがありません。Supabase の SQL Editor で supabase/migrations/20250125000000_site_auth_roles.sql を実行してください。'
          : insertError.message || 'プロフィールの作成に失敗しました。';
      throw new Error(msg);
    }
    currentProfile = { name: null, role_id: DEFAULT_ROLE_ID };
  }

  const roleId = currentProfile.role_id ?? DEFAULT_ROLE_ID;

  const { data: roleRow } = await supabase
    .from('roles')
    .select('id, name, slug')
    .eq('id', roleId)
    .single();

  const role = roleRow
    ? { id: roleRow.id, name: roleRow.name, slug: roleRow.slug }
    : null;

  const { data: perms } = await supabase
    .from('role_permissions')
    .select('permission_id')
    .eq('role_id', roleId);

  const permIds = (perms ?? []).map((p) => p.permission_id);
  let permissionSlugs: string[] = [];
  if (permIds.length > 0) {
    const { data: slugs } = await supabase
      .from('permissions')
      .select('slug')
      .in('id', permIds);
    permissionSlugs = (slugs ?? []).map((s) => s.slug);
  }

  return {
    id: userId,
    email,
    name: currentProfile.name,
    role,
    permissions: permissionSlugs,
  };
}

export async function getSiteSession(): Promise<SiteAuthUser | null> {
  if (!isSupabaseConfigured() || !supabase) return null;

  const { data } = await supabase.auth.getSession();
  const user = data.session?.user;
  if (!user?.id || !user.email) return null;

  return ensureProfileAndGetUser(user.id, user.email);
}

export async function signInSite(email: string, password: string): Promise<SiteAuthUser> {
  if (!isSupabaseConfigured() || !supabase) {
    throw new Error('Supabaseが設定されていません。');
  }

  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
  if (!data.user?.id || !data.user.email) throw new Error('ログインに失敗しました。');

  try {
    const siteUser = await ensureProfileAndGetUser(data.user.id, data.user.email);
    if (!siteUser) throw new Error('プロフィールの取得に失敗しました。Supabase に roles, permissions, site_user_profiles テーブルがあるか確認してください。');
    return siteUser;
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('プロフィールの取得に失敗しました。');
  }
}

export async function signOutSite(): Promise<void> {
  if (isSupabaseConfigured() && supabase) {
    await supabase.auth.signOut();
  }
}
